import { site } from "@/config/site";
import { useTranslation } from "react-i18next";
import { PrivacyBrLogo } from "@/shared/components/ui";

function TelegramIcon({ className = "h-5 w-5 shrink-0" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

type HomeTwinCtasProps = { className?: string };

/** Privacy.com.br + Telegram (prévias), mesmo par da secção final. */
export function HomeTwinCtas({ className = "" }: HomeTwinCtasProps) {
  const { t } = useTranslation();
  const { links } = site;
  const previewsUrl = links.telegramPreviews?.trim() || links.telegram;

  return (
    <div
      className={`flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-5 ${className}`.trim()}
    >
      <a
        href={links.privacyAccess}
        target="_blank"
        rel="noopener noreferrer"
        className={`${focusRing} focus-visible:outline-orange-300 group relative inline-flex min-h-[3rem] min-w-[12rem] items-center justify-center gap-3 overflow-hidden rounded-xl px-7 py-3 text-[calc(0.9rem+0.05vw)] font-semibold tracking-tight text-white shadow-[0_10px_36px_-10px_rgba(234,88,12,0.55),inset_0_1px_0_rgba(255,255,255,0.22)] transition-[transform,box-shadow,filter] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_16px_44px_-12px_rgba(234,88,12,0.65),inset_0_1px_0_rgba(255,255,255,0.28)] hover:brightness-[1.04] active:translate-y-0 active:scale-[0.99] motion-reduce:transform-none bg-gradient-to-b from-orange-400 via-orange-500 to-orange-600 border border-white/20`}
        aria-label={`${t("cta.privacyBrand")}: ${t("cta.privacyAccessLabel")}`}
      >
        <span
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          aria-hidden
          style={{
            background:
              "radial-gradient(120% 80% at 50% -20%, rgba(255,255,255,0.35), transparent 55%)",
          }}
        />
        <PrivacyBrLogo
          decorative
          className="relative h-[21px] w-auto shrink-0 text-white drop-shadow-sm"
          title={t("cta.privacyBrand")}
        />
        <span className="relative text-[calc(0.9rem+0.05vw)]">{t("cta.privacyAccessLabel")}</span>
      </a>
      <a
        href={previewsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${focusRing} focus-visible:outline-[#5fc2f7] group inline-flex min-h-[3rem] min-w-[12rem] items-center justify-center gap-3 rounded-xl border-2 border-[#2AABEE]/90 bg-[#2AABEE]/[0.07] px-7 py-3 text-[calc(0.9rem+0.05vw)] font-semibold tracking-tight text-[#7dd3fc] shadow-[0_8px_28px_-8px_rgba(42,171,238,0.4),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-[2px] transition-[transform,box-shadow,background-color,color,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-[#2AABEE] hover:bg-[#2AABEE] hover:text-white hover:shadow-[0_14px_40px_-10px_rgba(42,171,238,0.5)] active:translate-y-0 active:scale-[0.99] motion-reduce:transform-none`}
        aria-label={t("cta.telegramPreviews")}
      >
        <TelegramIcon className="h-[1.15rem] w-[1.15rem] shrink-0 opacity-90 transition-opacity duration-200 group-hover:opacity-100" />
        {t("cta.telegramPreviews")}
      </a>
    </div>
  );
}
